﻿/* https://brandplatform.smartphoto.com/general/popups/ai-multiple-filters/ */
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { showLoadingDialog } from "./loading-dialog";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
import { IPopup } from "../technical/popup-new";
import { createPopupLayout } from "../layout/popup";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
} from "../../common/html";
import { IPhoto, PhotoSourcesPopup } from "../organisms/photo-sources-popup";
import { getParameter, updateParameter } from "../../utils/querystring";
import { initDisableContextMenu } from "../technical/disable-context-menu";
import { PhotoImport, sendImportPhotosFinishedCreatorEvent } from "../technical/creator-events";
import { SmartphotoPhoto, StoreType } from "../../services/storageService";
import { FlowType } from "../technical/upload";
import { initSlider, SliderComponent } from "../organisms/slider";

export interface IAiMultipleFiltersDependencies {
	close?: () => void;
	error?: (message: string) => void;
	selectedFilterQuery?: string;
	embed?: boolean;
}

export function createAiMultipleFilters(
	deps: IAiMultipleFiltersDependencies,
	pvc: string,
	design: string,
	theme?: string,
	quantityString?: string,
	aiFilterCodes?: string,
	isSampleProduct?: boolean,
) {
	enum QueueStatus {
		Undefined,
		Pending,
		Generated,
		Downloading,
		Followup,
		Done,
		Error,
	}

	const buttonDisabledClass = "a-action--disabled";
	const activeUploadsClass = "m-product-tile--selected";
	const activeUploadsCheckmarkClass = "a-wishlist-button--checked";
	const activeFilterClass = "m-product-tile--selected";
	const activeHistoryClass = "m-product-tile--selected";
	const disabledFilterClass = "m-product-tile--forbidden";

	const eventDisposables: IDisposable = compositeDisposable(undefined);
	let attemptsLeft = 0;
	const activePollers: NodeJS.Timeout[] = [];

	let uploadsSliderComp: SliderComponent;
	let historySliderComp: SliderComponent;

	const updateNextStepUrl = (popupEl: HTMLElement) => {
		const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);
		const allowMultipleUpload = uploadButton.dataset.allowMultiple === "true";

		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
		continueButtonEls.forEach((continueButton) => {
			let updatedHref = continueButton.getAttribute("href");
			// Add all the history photos as a selection to the user
			const historyEls = expressQuerySelectorAll<HTMLElement>(
				popupEl,
				".technical-aifilter-history[data-photoid]:not(.technical-aifilter-history-template)",
			);
			// only select the photoid's from the history that are already loaded
			const selectionIds = historyEls.filter((x) => x.dataset.photourl).map((x) => x.dataset.photoid);
			const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
			const currentPhotoId = mainPhoto.dataset.photoId;
			updatedHref = updateParameter(
				"photoids",
				allowMultipleUpload
					? currentPhotoId + "," + selectionIds.filter((x) => x !== currentPhotoId).join(",")
					: currentPhotoId, // current photoid in front -> always filled as the first, then sorted by date created
				updatedHref,
			);

			if (theme) updatedHref = updateParameter("theme", theme, updatedHref);
			if (isSampleProduct) updatedHref = updateParameter("isSampleProduct", "true", updatedHref);

			updatedHref = updateParameter("selectionids", selectionIds.join(","), updatedHref);
			continueButton.setAttribute("href", updatedHref);
			continueButton.classList.toggle(
				buttonDisabledClass,
				!historyEls.find((x) => x.dataset.photourl && x.dataset.photoid == currentPhotoId),
			);
		});
	};

	const swapButtonText = (button: HTMLElement, text: string) => {
		const textEl = expressQuerySelector(button, ".technical-text");
		if (textEl) textEl.textContent = text;
	};

	const swapPhoto = (popupEl: HTMLElement, photoId: string, photoUrl: string) => {
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
		const emptyStateEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-emptystate", true);
		const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", true);
		mainPhoto.src = photoUrl;
		mainPhoto.removeAttribute("data-pendingid");
		mainPhoto.dataset.photoId = photoId;
		expressRemoveClass(mainPhoto, "u-hide");
		expressAddClass(emptyStateEl, "u-hide");

		updateNextStepUrl(popupEl);

		loading.classList.add("u-hide");
	};

	// This method shows an error because something went wrong
	const showError = (
		popupEl: HTMLElement,
		pageLogComponent: IPageLog,
		pendingId: string,
		errorMessage: string,
		filterNumber: string,
	) => {
		if (!errorMessage) throw new Error("pendingError called without errorMessage");
		const matchingFilter = expressQuerySelector<HTMLElement>(
			popupEl,
			`.technical-aifilter-filter[data-filternumber='${filterNumber}']`,
			false,
		);
		const matchingFilterTitleEl =
			matchingFilter && expressQuerySelector<HTMLElement>(matchingFilter, ".technical-filter-name", false);

		// reset main photo
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
		if (mainPhoto.dataset.pendingid == pendingId || mainPhoto.dataset.pendingid === "null") {
			mainPhoto.removeAttribute("data-pendingid");
			const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
			loading.classList.add("u-hide");
		}

		// reset history
		const historyEl = expressQuerySelector<HTMLImageElement>(
			popupEl,
			`.technical-aifilter-history[data-filternumber="${filterNumber}"]`,
			false,
		);
		historyEl && historyEl.remove();

		// reset selected filter
		const selectedFilter = expressQuerySelector(
			popupEl,
			`.technical-aifilter-filter[data-filternumber="${filterNumber}"]`,
			false,
		);
		selectedFilter && selectedFilter.classList.remove("o-ai-multiple-filters__filter-container--active");

		// set pagelog
		pageLogComponent.updateMessageAndStyle(
			PageLogStyle.Error,
			errorMessage.replace("{0}", matchingFilterTitleEl?.innerText || ""),
			false,
		);
	};

	// This method will poll the server for a pending photo in the history
	const pollForPending = async (popupEl: HTMLElement, pageLogComponent: IPageLog, pendingId: string) => {
		fetch("/apicore/aifilter/aipoll", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ id: pendingId, aiFilterCodes: aiFilterCodes }),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data && data.status === QueueStatus.Followup) {
					// Followup means the AI task was done, but a new one was started. Example VertexAI first, then RemoveBackground.
					// In this case, we want to update the pendingId with the new one and keep polling until we get a "done" status.
					// Change all pending IDs in the UI to the new one
					const historyEl = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-partnerid='${pendingId}']`,
						false,
					);
					if (historyEl) historyEl.dataset.partnerid = data.newPendingId;

					const mainPhoto = expressQuerySelector<HTMLImageElement>(
						popupEl,
						`.technical-aifilter-main[data-pendingid='${pendingId}']`,
						false,
					);
					if (mainPhoto) mainPhoto.dataset.pendingid = data.newPendingId;

					// start polling on the new ID
					const timeoutId = setTimeout(() => {
						activePollers.splice(activePollers.indexOf(timeoutId), 1);
						pollForPending(popupEl, pageLogComponent, data.newPendingId);
					}, 1500);
					activePollers.push(timeoutId);
				} else if (data && data.status === QueueStatus.Done) {
					// When done, show the photo in the history
					const historyEl = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-partnerid='${pendingId}']`,
						false,
					);
					if (historyEl) {
						const loadingEl = expressQuerySelector<HTMLElement>(historyEl, ".technical-photo-loading", false);
						const historyImage = expressQuerySelector<HTMLImageElement>(historyEl, "img", true);
						historyImage.classList.remove("u-hide");
						historyImage.src = data.url;
						historyEl.dataset.photoid = data.id;
						historyEl.dataset.photourl = data.url;
						if (loadingEl) loadingEl.classList.add("u-hide");
					}

					// If the main photo is waiting for this pending, swap it + change the CTA URL
					const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
					if (mainPhoto.dataset.pendingid == pendingId) swapPhoto(popupEl, data.id, data.url);
					else updateNextStepUrl(popupEl);
				} else if (data && data.errorMessage) {
					const matchingHistoryPhoto = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-photoid='${pendingId}']`,
						false,
					);
					showError(
						popupEl,
						pageLogComponent,
						pendingId,
						data.errorMessage,
						matchingHistoryPhoto?.dataset.filternumber,
					);
				} else {
					const timeoutId = setTimeout(() => {
						activePollers.splice(activePollers.indexOf(timeoutId), 1);
						pollForPending(popupEl, pageLogComponent, pendingId);
					}, 1500);
					activePollers.push(timeoutId);
				}
			});
	};

	const showStartedTaskAndPoll = async (
		popupEl: HTMLElement,
		filterGroupNumber: number,
		photoId: string,
		pendingId: string,
		pageLogComponent: IPageLog,
		count: number,
	) => {
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
		const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", true);

		/* History photos */
		// History: Make a copy of the template
		const historyEl = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-history-template", true);
		const newHistorySlideEl = historyEl.cloneNode(true) as HTMLImageElement;
		const newHistoryEl = expressQuerySelector<HTMLElement>(newHistorySlideEl, ".technical-aifilter-history", true);

		// History: Remove active class of other history items + set as active only if it is active on the main photo
		if (mainPhoto.dataset.originalPhotoId === photoId) {
			// Change the main picture to the original one
			mainPhoto.setAttribute("data-pendingid", pendingId);
			loading.classList.remove("u-hide");

			deselectActiveHistory(popupEl);
			newHistoryEl.classList.add(activeHistoryClass);
		}
		newHistorySlideEl.classList.remove("u-hide");
		newHistorySlideEl.classList.remove("technical-aifilter-history-template");

		// History: Set attributes of the newly created history div
		newHistoryEl.dataset.filternumber = filterGroupNumber.toString();
		newHistoryEl.removeAttribute("data-photoid");
		newHistoryEl.removeAttribute("data-photourl");
		newHistoryEl.dataset.originalphotoid = photoId || "";
		newHistoryEl.dataset.partnerid = pendingId;

		// History: Set the image to loading of the new history element
		const historyImage = expressQuerySelector<HTMLImageElement>(newHistoryEl, "img", true);
		historyImage.classList.add("u-hide");
		const loadingEl = expressQuerySelector<HTMLElement>(newHistoryEl, ".technical-photo-loading", false);
		if (loadingEl) loadingEl.classList.remove("u-hide");

		// History: Add the item to the DOM
		historySliderComp.addSlide(0, newHistorySlideEl);

		return new Promise<void>((resolve, reject) => {
			pollForPending(popupEl, pageLogComponent, pendingId);
		});
	};

	let startTaskIsRunning = false;
	const startTask = async (
		popupEl: HTMLElement,
		aiFilters: HTMLElement[],
		pageLog: HTMLElement,
		pageLogComponent: IPageLog,
		photoIds: string[],
		filterGroupNumber: number,
	) => {
		if (startTaskIsRunning) return;

		startTaskIsRunning = true;
		const uploadButton = expressQuerySelector<HTMLButtonElement>(popupEl, ".technical-aifilter-upload", true);
		if (pageLog.dataset.filterNumber === filterGroupNumber.toString()) pageLogComponent.hideLog();

		// Check if we have 0 attempts left, if so, block action (visual cue needed? Not yet)
		if (attemptsLeft === 0) return;

		// Set the current filter active - if we have any
		aiFilters.forEach((aiFilter) => {
			if (aiFilter.dataset.filternumber === filterGroupNumber.toString()) aiFilter.classList.add(activeFilterClass);
			else aiFilter.classList.remove(activeFilterClass);
		});

		// Set the main photo to show the current task, with a loading div
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
		const emptyStateEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-emptystate", false);
		mainPhoto.src = "";
		expressAddClass(mainPhoto, "u-hide");
		mainPhoto.dataset.originalPhotoId = photoIds[0];
		expressAddClass(emptyStateEl, "u-hide");

		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
		continueButtonEls.forEach((continueButton) => swapButtonText(continueButton, continueButton.dataset.labelcontinue));

		fetch("/apicore/aifilter/starttask", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				originalPhotoIds: photoIds,
				pvc,
				chosenFilterGroupNumber: filterGroupNumber,
				aiFilterCodes: aiFilterCodes,
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				const pendingId = data.pendingIds[0] || null;

				// error during start of task
				if (data.errorMessage) {
					showError(popupEl, pageLogComponent, pendingId, data.errorMessage, filterGroupNumber.toString());
					if (!pendingId) return;
				}

				attemptsLeft = data.attemptsLeft;
				const attemptsLabelEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-attempts", false);
				updatePendingLabels(attemptsLabelEl, data.label, data.attemptsLeft);

				// When no attempts are left, disable the upload button and disable the filters
				if (attemptsLeft === 0) {
					aiFilters.forEach((aiFilter) => aiFilter.classList.add(disabledFilterClass));
				}

				// Disable the buttons
				continueButtonEls.forEach(
					(continueButton) => continueButton.href && continueButton.classList.add(buttonDisabledClass),
				);

				const promises = [];
				for (let i = 0; i < data.pendingIds.length; i++) {
					promises.push(
						showStartedTaskAndPoll(popupEl, filterGroupNumber, photoIds[i], data.pendingIds[i], pageLogComponent, i),
					);
				}

				startTaskIsRunning = false;
				return Promise.all(promises);
			});
	};

	const updatePendingLabels = (attemptsLabelEl: HTMLElement, attemptsText?: string, amountLeft?: any) => {
		// Update the # attempts label
		if (!attemptsText || !attemptsLabelEl) return;
		expressQuerySelector<HTMLElement>(attemptsLabelEl, "p", true).textContent = attemptsText;
		attemptsLabelEl.classList.remove("u-hide");

		const attemptsLabelElText = expressQuerySelector<HTMLElement>(
			attemptsLabelEl,
			".technical-aifilter-attempts-text",
			false,
		);

		if (!attemptsLabelElText) return;
		const hasAttemptsLeft = amountLeft > 0;
		attemptsLabelElText.classList.toggle("u-background-color-style--orange-500", !hasAttemptsLeft);
		attemptsLabelElText.classList.toggle("u-background-color-style--grey-60", hasAttemptsLeft);
	};

	const deselectActiveFilters = (popupEl: HTMLElement) => {
		const activeFilters = expressQuerySelectorAll<HTMLElement>(
			popupEl,
			".technical-aifilter-filter." + activeFilterClass,
		);
		activeFilters.forEach((activeFilter) => activeFilter.classList.remove(activeFilterClass));
	};

	const deselectActiveHistory = (popupEl: HTMLElement) => {
		const activeHistory = expressQuerySelectorAll<HTMLElement>(
			popupEl,
			".technical-aifilter-history." + activeHistoryClass,
		);
		activeHistory.forEach((activeHistory) => activeHistory.classList.remove(activeHistoryClass));
	};

	const deselectActiveFiltersAndHistory = (popupEl: HTMLElement) => {
		deselectActiveFilters(popupEl);
		deselectActiveHistory(popupEl);
	};

	const toggleUpload = (uploadEl: HTMLElement, force?: boolean) => {
		// set the blue border
		uploadEl.classList.toggle(activeUploadsClass, force);

		// set the checkmark
		expressQuerySelector<HTMLElement>(uploadEl, ".technical-button", true).classList.toggle(
			activeUploadsCheckmarkClass,
			force,
		);
	};

	const onInit = (containerEl: HTMLElement) => {
		window.eXpress = {};
		window.eXpress.getUsedPhotoIds = () => {
			const uploadEls = expressQuerySelectorAll<HTMLLinkElement>(
				containerEl,
				".technical-aifilter-uploads-slider .technical-slider-slide",
			);

			return uploadEls.map((u) => u.dataset.photoId);
		};
		// window.eXpress.getUsedPhotoParentIds = () => {
		// 	return photoData.photoParentIds;
		// };
		// window.eXpress.getUsedPhotoNames = () => {
		// 	return photoData.originalFilenames;
		// };

		// Set attempts left (attribute on the popup)
		attemptsLeft = parseInt(containerEl.dataset.attemptsLeft) || 0;

		// Load the main picture if it is pending
		const pageLog = expressQuerySelector<HTMLElement>(containerEl, ".technical-top-message-log", true);
		const pageLogComponent = createPageLog(pageLog);
		const mainPhoto = expressQuerySelector<HTMLImageElement>(containerEl, ".technical-aifilter-main", false);
		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(containerEl, ".technical-aifilter-continue");
		const uploadButton = expressQuerySelector<HTMLButtonElement>(containerEl, ".technical-aifilter-upload", true);

		// Init the uploads slider
		const uploadsSlider = expressQuerySelector<HTMLElement>(
			containerEl,
			".technical-aifilter-uploads-slider .technical-slider",
			false,
		);
		if (uploadsSlider) uploadsSliderComp = initSlider(uploadsSlider);

		// Init the filter slider
		const filterSlider = expressQuerySelector<HTMLElement>(
			containerEl,
			".technical-aifilter-filter-slider .technical-slider",
			false,
		);
		if (filterSlider) initSlider(filterSlider);

		// Init the history slider
		const historySlider = expressQuerySelector<HTMLElement>(
			containerEl,
			".technical-aifilter-history-slider .technical-slider",
			false,
		);
		if (historySlider) historySliderComp = initSlider(historySlider);

		// Add clicks on the uploaded photos
		eventDisposables.add(
			expressEventListener(uploadsSlider, "click", (e) => {
				const uploadDiv = e.target.closest(".technical-aifilter-uploads");
				if (!uploadDiv) return;

				// We clicked a photo, select it or deselect it in the active list
				const clickedPhotoId = uploadDiv.dataset.photoId;
				if (uploadDiv && uploadDiv.dataset.photoId) {
					const idx = activePhotoIds.indexOf(clickedPhotoId);
					if (idx === -1) {
						activePhotoIds.push(clickedPhotoId);
					} else {
						activePhotoIds.splice(idx, 1);
					}
					toggleUpload(uploadDiv);
				}

				// Set the label to show how many are selected
				const selectedEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-aifilter-photos-selected", true);
				selectedEl.innerText = selectedEl.dataset.label.replace("{0}", activePhotoIds.length.toString());

				// Filters: 0 photos? Disable the filters otherwise enable
				// Also remove their selection class
				const aiFilters = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-aifilter-filter");
				aiFilters.forEach((aiFilter) => {
					aiFilter.classList.remove(activeFilterClass);
					if (activePhotoIds.length === 0 || attemptsLeft == 0) aiFilter.classList.add(disabledFilterClass);
					else aiFilter.classList.remove(disabledFilterClass);
				});
			}),
		);

		enum ContinueButtonLabel {
			Continue,
			SelectFilter,
			SelectGenerated,
		}

		let activePhotoIds: string[] = [];
		if (mainPhoto) {
			// Add upload button functionality
			const initPhotoUpload = () => {
				if (uploadButton.disabled) return;
				pageLogComponent.hideLog(); // reset page log

				const allowMultipleUpload = uploadButton.dataset.allowMultiple === "true";
				const folderId = uploadButton.dataset.folderId;

				const onImportPhotosFinished = async (photos: IPhoto[], failedPollingFileNames: string[]): Promise<void> => {
					if (!photos || photos.length === 0) {
						return;
					}

					// Make sure the empty state label is the correct one
					const emptyStateLabelEl = expressQuerySelector<HTMLElement>(
						containerEl,
						".technical-empty-state-label",
						true,
					);
					emptyStateLabelEl.innerText = emptyStateLabelEl.dataset.selectafilterfirst;

					// Active uploads: Remove active class of other history items + set as active
					const activeUploads = expressQuerySelectorAll<HTMLElement>(
						uploadsSliderComp.sliderEl,
						".technical-aifilter-uploads",
					);
					activeUploads.forEach((activeUpload) => toggleUpload(activeUpload, false));

					// Fetch the template
					const uploadsEl = expressQuerySelector<HTMLImageElement>(
						containerEl,
						".technical-aifilter-uploads-template",
						true,
					);

					// Add the photos as slides to the uploadsSlider
					photos.forEach((photo) => {
						// History: Make a copy of the template
						const newUploadsEl = uploadsEl.cloneNode(true) as HTMLElement;
						const innerUploadDiv = expressQuerySelector<HTMLElement>(newUploadsEl, ".technical-aifilter-uploads", true);

						// Set it to checked
						toggleUpload(innerUploadDiv, true);

						// Make it a real item
						newUploadsEl.classList.remove("u-hide");
						newUploadsEl.classList.remove("technical-aifilter-uploads-template");

						// Set attributes of the newly created upload div but on the inner div
						innerUploadDiv.dataset.originalFilename = photo.originalFilename;
						innerUploadDiv.dataset.photoId = photo.fileId;
						innerUploadDiv.dataset.photoUrl = photo.mediumUrl;

						// Set the image
						const image = expressQuerySelector<HTMLImageElement>(innerUploadDiv, "img");
						image.src = photo.thumbUrl;

						// History: Add the item to the DOM
						uploadsSliderComp.addSlide(0, newUploadsEl);
					});

					activePhotoIds = photos.map((p) => p.fileId);

					// Set the label to show how many are selected
					const selectedEl = expressQuerySelector<HTMLElement>(
						containerEl,
						".technical-aifilter-photos-selected",
						true,
					);
					selectedEl.innerText = selectedEl.dataset.label.replace("{0}", activePhotoIds.length.toString());

					swapButtonText(uploadButton, uploadButton.dataset.labeluploadanother);
					updateContinueButtonLabels(ContinueButtonLabel.SelectFilter);
					deselectActiveFiltersAndHistory(containerEl);

					if (attemptsLeft > 0) {
						const disabledAiFilters = expressQuerySelectorAll<HTMLElement>(containerEl, `.${disabledFilterClass}`);
						disabledAiFilters.forEach((filter) => expressRemoveClass(filter, disabledFilterClass));
					}
				};

				// disable the button
				uploadButton.disabled = true;

				const photoSources = new PhotoSourcesPopup(
					FlowType.AiFlow,
					onImportPhotosFinished,
					allowMultipleUpload ? 99 : 1,
					[],
					folderId,
				);
				photoSources.openPhotoStoresPopup({
					onBeforeClose: () => {
						uploadButton.disabled = false;
					},
				});
			};

			if (uploadButton) {
				eventDisposables.add(expressEventListener(uploadButton, "click", () => initPhotoUpload()));
			}

			// Enable the history clicks
			const historyContainer = expressQuerySelector<HTMLElement>(
				containerEl,
				".technical-aifilter-history-slider",
				false,
			);
			if (historyContainer) {
				// Add polling for the pending images
				const pendingHistory = expressQuerySelectorAll<HTMLLinkElement>(
					historyContainer,
					".technical-aifilter-history[data-partnerid]",
				);
				pendingHistory.forEach((historyItem) => {
					const pendingId = historyItem.dataset.partnerid;
					pollForPending(containerEl, pageLogComponent, pendingId);
				});

				// Add click to swap the foto to the one from the history
				eventDisposables.add(
					expressEventListener(historyContainer, "click", (e) => {
						const historyDiv = e.target.closest(".technical-aifilter-history");
						if (historyDiv && historyDiv.dataset.photoid) {
							swapPhoto(containerEl, historyDiv.dataset.photoid, historyDiv.dataset.photourl);
							deselectActiveHistory(containerEl);

							// Select the clicked history as being active
							historyDiv.classList.add(activeHistoryClass);

							updateContinueButtonLabels(ContinueButtonLabel.Continue);
						}
					}),
				);
			}

			const updateContinueButtonLabels = (label: ContinueButtonLabel) => {
				// Update the label of the buttons
				continueButtonEls.forEach((continueButton) => {
					if (label === ContinueButtonLabel.Continue)
						swapButtonText(continueButton, continueButton.dataset.labelcontinue);
					if (label === ContinueButtonLabel.SelectFilter) {
						swapButtonText(continueButton, continueButton.dataset.labelselectfilter);
						// disable the continue button => first a filter or history item needs to be selected to continue to the CRE
						continueButton.classList.add(buttonDisabledClass);
					}
					if (label === ContinueButtonLabel.SelectGenerated)
						swapButtonText(continueButton, continueButton.dataset.labelselectgenerated);
				});
			};

			// Enable the filter clicks
			const enableFilters = () => {
				const aiFilters = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-aifilter-filter");
				aiFilters.forEach((aiFilter) => {
					eventDisposables.add(
						expressEventListener(aiFilter, "click", (e) => {
							if ((e.currentTarget as HTMLElement).classList.contains(disabledFilterClass)) return;

							startTask(
								containerEl,
								aiFilters,
								pageLog,
								pageLogComponent,
								activePhotoIds,
								parseInt(aiFilter.dataset.filternumber) || 0,
							);
						}),
					);
				});
			};

			enableFilters();

			const pendingId = mainPhoto.dataset.pendingid || "";
			if (pendingId && pendingId !== "") {
				return new Promise<void>((resolve, reject) => {
					pollForPending(containerEl, pageLogComponent, pendingId);
				});
			}

			const continueBtns = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-aifilter-continue");
			continueBtns.forEach((continueBtn) => {
				eventDisposables.add(
					expressEventListener(continueBtn, "click", (e: MouseEvent) => {
						if (continueBtn.classList.contains(buttonDisabledClass)) {
							e.preventDefault();
							return;
						}
						showLoadingDialog(Promise.resolve(), 0, 10000);
						if (!deps.embed) return;
						e.preventDefault();
						const url = continueBtn.getAttribute("href");
						if (!url) return;
						const photoIds = getParameter("photoids", url);
						if (!photoIds) return;
						const photoIdArray = photoIds.split(",");
						const bc = new BroadcastChannel("connectedToStorePartner");
						const photos: SmartphotoPhoto[] = [];
						photoIdArray.forEach((photoId) => {
							photos.push({ fileId: photoId } as SmartphotoPhoto);
						});
						sendImportPhotosFinishedCreatorEvent(bc, {
							selection: photos,
							storeType: StoreType.eXpress,
						} as PhotoImport);
					}),
				);
			});
		}
	};

	const initEmbed = async () => {
		const containerEl = expressQuerySelector<HTMLElement>(document, ".technical-ai-filter-page", true);
		await onInit(containerEl);
		initDisableContextMenu(containerEl);
	};

	const init = async () => {
		let aiPhotoPopup: IPopup;
		await showLoadingDialog(
			createPopupLayout({
				dialogId: "showaiphotopopup_" + pvc + "_" + design,
				view: "PhotoWithAiFilter",
				onClose: () => {
					// Stop all active pollers
					activePollers.forEach((poller) => clearTimeout(poller));
					deps.close();
				},
				onBeforeOpen: (popupEl: HTMLElement) => onInit(popupEl),
				forceRefresh: true, // refresh to reset api errors in popup
				data: {
					Pvc: pvc,
					Design: design,
					QuantityString: quantityString,
					AiFilterCodes: aiFilterCodes,
				},
			}).then((r) => {
				aiPhotoPopup = r;
				if (aiPhotoPopup) {
					aiPhotoPopup.openAsync();
				}
			}),
		);

		if (aiPhotoPopup.popup.dataset.hasError) {
			if (aiPhotoPopup.popup.dataset.showOnPagePagelog) {
				deps.error && deps.error(aiPhotoPopup.popup.dataset.errorMessage);
				return;
			}
			// something went wrong => open select photo dialog again
			return Promise.reject(aiPhotoPopup.popup.dataset.errorMessage);
		}

		await aiPhotoPopup.openAsync();

		initDisableContextMenu(aiPhotoPopup.popup);
	};

	deps.embed ? initEmbed() : init();

	return {
		dispose: () => {
			eventDisposables.dispose();
		},
	};
}
